import { Dialog, LinearProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BrandItemMasterImagesQuery, useBrandItemMasterImagesQuery } from "../../../Queries";

// A single masterdata image candidate as returned by the shared Core resolver.
// These are candidates - not necessarily existing BrandItemImage rows - and imageId may be missing
// for MAS/masterdata-service images that only expose an imageUrl plus metadata.
export type MasterImage = NonNullable<NonNullable<BrandItemMasterImagesQuery["BrandItemMasterImages"]>[number]>;

type BrandItemImportDialogProps = {
   brandItemId: string;
   onSelect: (image: MasterImage) => void;
   onHide: () => void;
};

export const BrandItemImportDialog = ({ brandItemId, onHide, onSelect }: BrandItemImportDialogProps) => {
   return (
      <Dialog open={true} onClose={onHide} fullWidth={true} maxWidth={"lg"}>
         <MasterImages brandItemId={brandItemId} onSelect={onSelect} />
      </Dialog>
   );
};

type MasterImagesProps = {
   brandItemId: string;
   onSelect: (image: MasterImage) => void;
};

const Container = styled("div")({
   display: "flex",
   flexDirection: "row",
   flexWrap: "wrap",
   gap: "10px",
});

const Image = styled("div")({
   position: "relative",
   color: "transparent",
   "&:hover *": {
      color: "white",
      display: "flex",
      flexDirection: "column",
   },
});

const ImageText = styled("div")({
   fontSize: "11px",
   position: "absolute",
   bottom: 0,
   left: "20px",
   right: "20px",
   display: "none",
   backgroundColor: "#000000",
   opacity: "0.5",
   padding: "5px 10px 5px 10px",
   minHeight: "40px",
});

const MasterImages = ({ brandItemId, onSelect }: MasterImagesProps) => {
   const { data } = useBrandItemMasterImagesQuery({
      variables: {
         brandItemId: brandItemId,
      },
   });

   if (!data) {
      return <LinearProgress />;
   }

   const images = data.BrandItemMasterImages || [];

   return (
      <Container>
         {images.length === 0 && <div>No images found</div>}
         {images.map((img, index) => {
            if (!img) {
               return null;
            }
            return (
               <Image
                  key={"master_image_" + (img.imageId || img.externId || index)}
                  onClick={() => onSelect(img)}
                  style={{
                     backgroundImage: "url(" + img.imageUrl + ")",
                     backgroundRepeat: "no-repeat",
                     backgroundSize: "contain",
                     backgroundPosition: "center",
                     height: "380px",
                     width: "380px",
                     cursor: "pointer",
                  }}
               >
                  <ImageText>
                     {img.color && <div style={{ marginBottom: "10px" }}>Color: {img.color}</div>}
                     {img.type && <div>Type: {img.type}</div>}
                     {img.perspective && <div>Perspective: {img.perspective}</div>}
                  </ImageText>
               </Image>
            );
         })}
      </Container>
   );
};
